export const USERCENTRICS_ANALYTICS_SERVICE =
  process.env.NEXT_PUBLIC_USERCENTRICS_POSTHOG_SERVICE ?? 'PostHog';

export const ANALYTICS_CONSENT_CHANGE_EVENT = 'aa-analytics-consent-change';

type UsercentricsService = {
  id?: string;
  name?: string;
  consent?: { given?: boolean; status?: boolean; type?: string };
};

type UsercentricsUi = {
  getServicesBaseInfo?: () => UsercentricsService[] | Promise<UsercentricsService[]>;
};

type UsercentricsConsent = {
  fromUserAction?: boolean;
  interaction?: string;
  type?: string;
  updatedBy?: string;
};

export type UsercentricsEventDetail = {
  action?: string;
  consent?: UsercentricsConsent;
  event?: string;
  type?: string;
  services?: Record<string, UsercentricsService> | UsercentricsService[];
  [key: string]: unknown;
};

declare global {
  interface Window {
    UC_UI?: UsercentricsUi;
  }
}

const INTERACTION_ACTIONS = new Set([
  'onAcceptAllServices',
  'onDenyAllServices',
  'onUpdateServices',
  'onEssentialChange',
  'onSessionRestored',
  'onMobileSessionRestore',
]);

function isImplicitInitialLoad(detail?: UsercentricsEventDetail) {
  return (
    detail?.action === 'onInitialPageLoad' ||
    detail?.type?.toLowerCase() === 'implicit' ||
    detail?.consent?.type?.toLowerCase() === 'implicit'
  );
}

export function hasUsercentricsInteraction(detail?: UsercentricsEventDetail) {
  if (isImplicitInitialLoad(detail)) {
    return false;
  }

  if (
    detail?.type?.toLowerCase() === 'explicit' ||
    detail?.consent?.type?.toLowerCase() === 'explicit' ||
    detail?.consent?.fromUserAction ||
    detail?.consent?.interaction === 'user'
  ) {
    return true;
  }

  const action = detail?.action ?? detail?.consent?.updatedBy;

  if (action && INTERACTION_ACTIONS.has(action)) {
    return true;
  }

  if (typeof document === 'undefined') {
    return false;
  }

  return document.cookie.split(';').some((part) => {
    const [name, value] = part.trim().split('=');
    return name === 'uc_user_interaction' && value === 'true';
  });
}

function getServiceConsent(service: UsercentricsService | undefined) {
  return service?.consent?.given ?? service?.consent?.status ?? null;
}

function findAnalyticsService(services: UsercentricsService[]) {
  return services.find(
    (service) =>
      service.name?.toLowerCase() === USERCENTRICS_ANALYTICS_SERVICE.toLowerCase() ||
      service.id === USERCENTRICS_ANALYTICS_SERVICE
  );
}

export async function getUsercentricsAnalyticsConsent(
  detail?: UsercentricsEventDetail
): Promise<boolean | null> {
  if (detail) {
    const serviceValue = detail[USERCENTRICS_ANALYTICS_SERVICE];

    if (typeof serviceValue === 'boolean') {
      return serviceValue;
    }

    const eventServices = Array.isArray(detail.services)
      ? detail.services
      : Object.values(detail.services ?? {});
    const eventConsent = getServiceConsent(findAnalyticsService(eventServices));

    if (eventConsent !== null) {
      return eventConsent;
    }
  }

  const services = (await window.UC_UI?.getServicesBaseInfo?.()) ?? [];
  return getServiceConsent(findAnalyticsService(services));
}
